/**
 * What each strategy input DOES, said directly beneath the input.
 *
 * SHARED ACROSS MODULES. Gifting, funeral bonds, annuities and equity release are means-test
 * levers, not aged care ones — they act on the assessment, so the same note is right wherever the
 * lever is offered. Aged Care and Support at Home both use these.
 *
 * WHY THESE EXIST. Every one of these consequences was explained only inside the floating
 * "What's happening" insight panel. So a client could type $200,000 into the gifting box, or
 * $30,000 into a funeral bond, and read nothing at all about what it costs them — unless they
 * happened to open a panel somewhere else on the screen and scroll to the right card.
 *
 * The app already had the better pattern in exactly one place: enter a share sale to fund a deposit
 * and the capital gains tax appears under that asset, at the moment it becomes true. Owner
 * direction, 2026-08-28 — do that everywhere, and retire the floating panel.
 *
 * THE RULE THESE FOLLOW. A note here says what becomes true because of the number in the field above
 * it. What the whole plan does to the client's position belongs in the plan summary, and saying it
 * in both places is how the insight panels came to repeat everything twice.
 */
import React from 'react';
import { FieldNote } from '../narrative/PlainWords.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { calculateDeprivedAssets } from '../../../../shared/src/calculation/giftingDeprivation.ts';
import {
    GIFTING_LIMIT_ANNUAL,
    GIFTING_LIMIT_5_YEAR,
    FUNERAL_BOND_LIMIT,
} from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { FactFindGiftsHistory } from '../../../../shared/src/calculation/factFindTypes.ts';

/**
 * A gift: how much of it actually leaves the means test, and how much Centrelink keeps counting.
 *
 * The figures come from the same `calculateDeprivedAssets` the pension assessment itself uses, so
 * the warning and the numbers beside it can never disagree — which they could, and did, when this
 * lived in a panel that recomputed its own version.
 */
export function GiftingFieldNote({
    giftingAmount,
    giftsHistory,
}: {
    giftingAmount: number;
    giftsHistory?: FactFindGiftsHistory;
}) {
    if (!(giftingAmount > 0)) return null;

    // What is already deprived from gifts made in earlier years, and what this gift adds on top.
    const before = calculateDeprivedAssets(giftsHistory, {});
    const after = calculateDeprivedAssets(giftsHistory, {
        otherStrategies: { gifting: true, giftingAmount },
    } as never);
    const added = Math.max(0, after - before);
    const leavesTheTest = Math.max(0, giftingAmount - added);

    if (added <= 0) {
        return (
            <FieldNote tone="good" heading="This gift leaves the means test straight away">
                All {formatCurrency(giftingAmount)} is within the free area, so Centrelink stops counting it
                now. The money is gone from the estate too — that is the cost of the improvement.
            </FieldNote>
        );
    }

    return (
        <FieldNote tone="warn" heading={`${formatCurrency(added)} of this is still counted for five years`}>
            <p>
                Only {formatCurrency(leavesTheTest)} leaves the means test now. The other{' '}
                <strong>{formatCurrency(added)}</strong> is treated as{' '}
                <strong>deprived</strong>: Centrelink goes on counting it as your asset, and deeming it to
                earn income, for five years from the date you give it away — even though the money has gone.
            </p>
            <p className="mt-1.5">
                The free areas are {formatCurrency(GIFTING_LIMIT_ANNUAL)} in a financial year and{' '}
                {formatCurrency(GIFTING_LIMIT_5_YEAR)} across any five years. Gifting more than that costs
                the capital immediately and buys nothing for five years.
            </p>
        </FieldNote>
    );
}

/**
 * A funeral bond: how much of it is actually exempt.
 *
 * THE LIMIT WAS WRONG WHERE THIS USED TO LIVE. The insight panel hard-coded $15,000 while the
 * assessment used the real published figure, so a client entering $15,500 was told the whole amount
 * was exempt and assessed as though it partly was not. This reads the same constant the assessment
 * does.
 */
export function FuneralBondFieldNote({
    amount,
    isCouple = false,
}: {
    amount: number;
    isCouple?: boolean;
}) {
    if (!(amount > 0)) return null;

    const exemptCap = FUNERAL_BOND_LIMIT * (isCouple ? 2 : 1);
    const excess = Math.max(0, amount - exemptCap);

    if (excess <= 0) {
        return (
            <FieldNote tone="good" heading="All of this is exempt from the assets test">
                A funeral bond is not counted, up to {formatCurrency(FUNERAL_BOND_LIMIT)} per person
                {isCouple ? ' — one bond each' : ''}. The money is still there for the family; Centrelink
                simply stops counting it, which can lift the Age Pension and lower the means-tested care fee.
            </FieldNote>
        );
    }

    return (
        <FieldNote tone="warn" heading={`${formatCurrency(excess)} of this is not exempt`}>
            Only {formatCurrency(exemptCap)} is exempt{isCouple ? ' (one bond each)' : ''}. The remaining{' '}
            <strong>{formatCurrency(excess)}</strong> is counted in the assets test like any other asset — so
            it is locked into a funeral bond without buying any means-test benefit.
        </FieldNote>
    );
}

/*
 * AnnuityFieldNote WAS HERE, AND IT IS GONE (26 Sep 2026).
 *
 * It stated what Centrelink counts and what the shape costs, in a panel of its own below the annuity
 * configurator — while the configurator's own "What this means for you" block said the same things
 * directly above it. The owner, reviewing the live screen: "surely we can have one section that
 * covers both... this way we are not duplicating information and creating heaps of stuff on the
 * screen." Everything it carried now sits behind the configurator's "How is this worked out?",
 * beside the figures it explains. The other notes in this file have no such duplicate and stay.
 */

/**
 * Borrowing against the home: what arrives, and what it grows into.
 *
 * A reverse mortgage is the one strategy on these screens where nothing is repaid along the way, so
 * the number that matters is not what is borrowed but what is owed at the end. That was in a card
 * called "Home Equity & Legacy Impact" that a client had no reason to open.
 */
export function ReverseMortgageFieldNote({
    loanAmount,
    interestRate,
    years = 5,
    useForRad = false,
}: {
    loanAmount: number;
    interestRate: number;
    years?: number;
    useForRad?: boolean;
}) {
    if (!(loanAmount > 0)) return null;

    const rate = (Number(interestRate) || 0) / 100;
    const owedLater = loanAmount * (1 + rate) ** years;
    const interest = owedLater - loanAmount;

    return (
        <FieldNote tone="warn" heading={`Nothing is repaid, so this grows to about ${formatCurrency(owedLater)} in ${years} years`}>
            <p>
                Borrowing {formatCurrency(loanAmount)} at {interestRate}% with no repayments adds roughly{' '}
                <strong>{formatCurrency(interest)}</strong> of interest over {years} years. The debt is
                settled from the house, so it comes out of what is left to the family.
            </p>
            {useForRad && (
                <p className="mt-1.5">
                    Against that: the money pays a refundable deposit, which removes that much daily
                    accommodation payment and is refunded when care ends. Whether borrowing leaves the family
                    ahead depends on the room price and how long the stay is — the projections settle it.
                </p>
            )}
        </FieldNote>
    );
}

/**
 * HEAS: the government scheme, and the two things that make it different from a commercial loan.
 */
export function HeasFieldNote({
    paymentAmount,
    paymentFrequency = 'fortnightly',
    interestRate,
    years = 5,
}: {
    paymentAmount: number;
    paymentFrequency?: string;
    interestRate: number;
    years?: number;
}) {
    if (!(paymentAmount > 0)) return null;

    const perYear = paymentAmount * (paymentFrequency === 'monthly' ? 12 : 26);
    const rate = (Number(interestRate) || 0) / 100;
    // Drawn steadily rather than as a lump, so each year's draw compounds for the years remaining.
    let owed = 0;
    for (let y = 0; y < years; y += 1) owed = (owed + perYear) * (1 + rate);

    return (
        <FieldNote tone="info" heading={`${formatCurrency(perYear)} a year, growing to about ${formatCurrency(owed)} owed in ${years} years`}>
            <p>
                The Home Equity Access Scheme is the government&apos;s own, at {interestRate}% — well below a
                commercial reverse mortgage — and the payments are{' '}
                <strong>not counted as income</strong> for the Age Pension. The home is kept.
            </p>
            <p className="mt-1.5">
                Nothing is repaid along the way, so the debt compounds and is settled from the house. You can
                stop or reduce the payments at any time.
            </p>
        </FieldNote>
    );
}
